import React, { useMemo, useState } from 'react';

import BoxContainer from '@src/components/BoxContainer';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { useServerReportPdf } from '@src/packages/dashboard/hooks/useServerReportPdf';

import TasksDashboardDrawers from './components/TasksDashboardDrawers';
import TasksDashboardHeader from './components/TasksDashboardHeader';
import TasksDetail from './components/TasksDetail';
import TasksFilterPanel from './components/TasksFilterPanel';
import TasksReportTabs, { type TasksReportTab } from './components/TasksReportTabs';
import { useTasksDashboard } from './hooks/useTasksDashboard';
import { useTasksSummarySort } from './hooks/useTasksSummarySort';
import styles from './styles.module.less';
import TasksSummary from './TasksSummary';
import { buildHistoryQuery } from './TasksSummary/historyQuery';
import { useExecutionsHistorySections } from './TasksSummary/hooks/useExecutionsHistorySections';
import type { TasksChartViews } from './TasksSummary/types';

export const TasksDashboardContent: React.FC = () => {
  const dashboard = useTasksDashboard();
  const [activeTab, setActiveTab] = useState<TasksReportTab>('summary');
  const [chartViews, setChartViews] = useState<TasksChartViews>({ evolution: 'bar', incidents: 'bar' });
  const summarySort = useTasksSummarySort();
  const onChartViewChange = (chart: keyof TasksChartViews, view: TasksChartViews[typeof chart]) => {
    setChartViews(current => ({ ...current, [chart]: view }));
  };
  // The summary sections live here so the screen and the PDF print the very same data.
  const query = useMemo(
    () => buildHistoryQuery(
      dashboard.chartFilters,
      dashboard.orgId,
      dashboard.queryDivisionIds,
      dashboard.timeZone,
    ),
    [dashboard.chartFilters, dashboard.orgId, dashboard.queryDivisionIds, dashboard.timeZone],
  );
  const history = useExecutionsHistorySections(query);
  const refreshSummary = () => history.refresh();

  const serverPdfAction = useServerReportPdf(
    query ? { ...query, reportType: 'tasks', variant: 'full', presentation: { ...chartViews, executionSort: summarySort.executionSort, complianceSort: summarySort.complianceSort } } : undefined,
  );

  return (
    <>
      <TasksFilterPanel
        title={dashboard.intl.formatMessage({ id: 'dashboards.chart.filters' })}
        filters={dashboard.filters}
        initialFilters={dashboard.initialFilters}
        chartFilters={dashboard.draftChartFilters}
        orgId={dashboard.orgId}
        divisionIds={dashboard.queryDivisionIds}
        disabledDate={dashboard.disabledDate}
        onFiltersChange={dashboard.setFilters}
        onTemplatesChange={dashboard.setSelectedTemplates}
        onApplyFilters={dashboard.applyFilters}
        hasPendingFilters={dashboard.hasPendingFilters}
        isApplyingFilters={dashboard.mainLoading}
      />
      <TasksDashboardHeader
        intl={dashboard.intl}
        isFilterPanelOpen={dashboard.isOpen}
        onToggleFilters={dashboard.toggle}
        onCustomFilterChange={dashboard.setFilters}
        serverPdfAction={serverPdfAction}
        onRefresh={activeTab === 'summary' ? refreshSummary : undefined}
        refreshDisabled={history.loading}
      />
      {/* The summary shows a skeleton per section; only the detail tab waits on this load. */}
      <BoxContainer loading={activeTab === 'detail' && dashboard.mainLoading} fullContent>
        <SecondaryPanel.Content>
          <ListViewportLayout
            viewportOffset={160}
            minHeight="calc(100dvh - 160px)"
            sidePanelOpen={dashboard.isOpen}
            constrainToScrollParent={false}
            top={<TasksReportTabs activeTab={activeTab} onChange={setActiveTab} />}
            topClassName={styles.tabsHeader}
          >
            {activeTab === 'summary' ? (
              <TasksSummary
                query={query}
                chartFilters={dashboard.chartFilters}
                sections={history.sections}
                onRetrySection={history.retry}
                chartViews={chartViews}
                onChartViewChange={onChartViewChange}
                {...summarySort}
              />
            ) : (
              <TasksDetail
                intl={dashboard.intl}
                tableDataByDay={dashboard.tableDataByDay}
                groupBy={dashboard.groupBy}
                columns={dashboard.columns}
              />
            )}
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      <TasksDashboardDrawers
        isTimelineOpen={dashboard.openTimelineEvents}
        organizationId={dashboard.orgId}
        divisionIds={dashboard.queryDivisionIds}
        onCloseTimeline={dashboard.onBackFromTimeline}
        isEventTypesOpen={dashboard.showEventTypeDrawer}
        onCloseEventTypes={() => dashboard.setShowEventTypeDrawer(false)}
        isUserListOpen={dashboard.drawerState.users}
        onCloseUserList={() => dashboard.toggleDrawer('users')}
        selectedUsers={dashboard.selectedUsers}
      />
    </>
  );
};

const TasksDashboard: React.FC = () => <TasksDashboardContent />;

export default TasksDashboard;
